import { CheckIcon, ChevronDownIcon, PlusIcon, XIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface CreatableOption {
  value: string;
  label: string;
  hint?: string;
}

/** Single select with search and "create" for small lists such as suppliers or locations. */
export function CreatableSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  createLabel,
  onCreate,
  id,
  clearable = true,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  options: CreatableOption[];
  placeholder: ReactNode;
  searchPlaceholder?: string;
  createLabel?: (text: string) => ReactNode;
  onCreate?: (text: string) => Promise<string>;
  id?: string;
  clearable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.value === value);
  const text = query.trim();
  const filtered = text
    ? options.filter((o) => `${o.label} ${o.hint ?? ""}`.toLowerCase().includes(text.toLowerCase()))
    : options;
  const exact = options.some((o) => o.label.toLowerCase() === text.toLowerCase());

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <div className="relative">
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            className="flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-input bg-surface px-3 text-left text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/25"
          >
            <span className={cn("truncate", !selected && "text-muted-foreground/80")}>
              {selected?.label ?? placeholder}
            </span>
            <ChevronDownIcon className="size-4 shrink-0 opacity-60" />
          </button>
        </PopoverTrigger>
        {clearable && selected ? (
          <button
            type="button"
            aria-label="Clear"
            onClick={() => onChange(null)}
            className="absolute top-1/2 right-8 flex size-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted"
          >
            <XIcon className="size-3.5" />
          </button>
        ) : null}
      </div>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-64 p-0">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={searchPlaceholder}
            autoFocus
          />
          <CommandList>
            <CommandGroup>
              {filtered.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <CheckIcon className={cn(option.value === value ? "opacity-100" : "opacity-0")} />
                  <span className="flex-1 truncate">{option.label}</span>
                  {option.hint ? (
                    <span className="truncate text-xs text-muted-foreground">{option.hint}</span>
                  ) : null}
                </CommandItem>
              ))}
              {onCreate && text && !exact ? (
                <CommandItem
                  value="__create"
                  onSelect={async () => {
                    const created = await onCreate(text);
                    onChange(created);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <PlusIcon />
                  {createLabel ? createLabel(text) : text}
                </CommandItem>
              ) : null}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
